/**
 * ContractsInvoices.jsx — Customers sub-tab
 *
 * Replaces Hazel's Invoice_generator.xlsm: a flat table 1:1 with the
 * "Invoice Tracker" sheet (Invoice / Date / Due / Amount / Customer / Send /
 * Paid Status / Notes / Invoice Record / Contract), plus a "Generate
 * Invoice" action that reproduces the old "RECORD INVOICE" macro end to
 * end — fills the real Implied invoice PDF template, uploads it, and
 * inserts the tracker row in one step (see backend/routes/invoices.js).
 *
 * Props:
 *   data     — from useSheetData (used for firm_id -> firm_name on the
 *              Contract dropdown; billing customers are a separate list,
 *              see billing_clients)
 *   getToken — from useAuth
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  fetchInvoices, generateInvoice, updateInvoice, deleteInvoice, createContractAndLinkInvoice, updateContract,
  fetchBillingClients, fetchContractsLite, uploadContractFile, linkContractFile,
  downloadInvoicePdfBlob, downloadContractFileBlob, uploadInvoiceFile, linkInvoiceFile,
} from '../api/backend.js';
import { saveBlobToDrive, pickDriveFolder } from '../utils/googleDrive.js';

/** Saves a Blob to the user's downloads folder — the plain "Download" path. */
function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const ONES = ['Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
  'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

/** 7 -> "Seven", 24 -> "Twenty-four". Falls back to the numeral above 99 or for non-integers. */
function numberToWords(n) {
  const num = Number(n);
  if (!Number.isInteger(num) || num < 0 || num > 99) return String(n);
  if (num < 20) return ONES[num];
  const tens = Math.floor(num / 10);
  const ones = num % 10;
  return ones === 0 ? TENS[tens] : `${TENS[tens]}-${ONES[ones].toLowerCase()}`;
}

/** 2026-06-01 -> "June 1, 2026" (matches the wording in Hazel's existing invoices). */
function fmtLongDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T00:00:00Z`);
  if (isNaN(d)) return '';
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function buildDescription(quantity, termStart, termEnd) {
  const qtyWords = numberToWords(quantity);
  const line1 = `Implied Platform for ${qtyWords} User${Number(quantity) === 1 ? '' : 's'}`;
  if (!termStart || !termEnd) return line1;
  return `${line1}\n${fmtLongDate(termStart)} - ${fmtLongDate(termEnd)}`;
}

function fmt(dateStr) {
  if (!dateStr) return '—';
  const raw = dateStr?.value ?? dateStr;
  const d = new Date(`${String(raw).slice(0, 10)}T00:00:00Z`);
  if (isNaN(d)) return raw;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function toInputDate(dateStr) {
  if (!dateStr) return '';
  const raw = dateStr?.value ?? dateStr;
  return String(raw).slice(0, 10);
}

function fmtMoney(n) {
  const num = Number(n) || 0;
  return `$${num.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function daysUntil(dateStr) {
  const v = toInputDate(dateStr);
  if (!v) return null;
  return Math.ceil((new Date(`${v}T00:00:00Z`).getTime() - Date.now()) / 86_400_000);
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ContractsInvoices({ data, getToken }) {
  const [invoices, setInvoices]   = useState([]);
  const [clients,  setClients]    = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading,  setLoading]    = useState(true);
  const [error,    setError]      = useState(null);
  const [showGenerate, setShowGenerate] = useState(false);
  const [busyAction, setBusyAction] = useState(null); // e.g. 'dl-inv_123' | 'drive-inv_123' — disables that row's button mid-request
  const [sortDir,  setSortDir]    = useState('desc');
  const [search,   setSearch]     = useState('');
  const [editingId, setEditingId] = useState(null); // invoice_id currently in row-edit mode, or null
  const [draft,     setDraft]     = useState({});   // in-progress edits for editingId
  const [pendingChanges, setPendingChanges] = useState(null); // { invoice_id, changes: [{key,label,before,after}] } — awaiting confirm
  const [openContractFor, setOpenContractFor] = useState(null); // invoice_id whose Contract-cell file popover is open
  const [openInvoiceFileFor, setOpenInvoiceFileFor] = useState(null); // invoice_id whose Invoice-PDF popover is open

  const firms = data?.firms ?? [];
  const firmMap = useMemo(() => {
    const m = {};
    for (const f of firms) if (f.firm_id) m[f.firm_id] = f;
    return m;
  }, [firms]);

  const load = useCallback(async () => {
    setError(null);
    // allSettled, not all — one of these three failing (e.g. a transient
    // contracts-lite hiccup) used to reject the whole reload and leave the
    // invoice table showing stale data with no visible feedback, so a fresh
    // "Generate Invoice" silently required a manual page refresh to show up.
    // Independent fetches mean the invoice table always reflects what just
    // happened even if one of the other two calls has a problem.
    const [invRes, cliRes, ctrRes] = await Promise.allSettled([
      fetchInvoices(getToken), fetchBillingClients(getToken), fetchContractsLite(getToken),
    ]);
    if (invRes.status === 'fulfilled') setInvoices(invRes.value ?? []);
    if (cliRes.status === 'fulfilled') setClients(cliRes.value ?? []);
    if (ctrRes.status === 'fulfilled') setContracts(ctrRes.value ?? []);

    const failed = [invRes, cliRes, ctrRes].find((r) => r.status === 'rejected');
    if (failed) setError('Some data failed to refresh: ' + failed.reason.message);
    setLoading(false);
  }, [getToken]);

  useEffect(() => { load(); }, [load]);

  const sortedInvoices = useMemo(() => {
    const rows = [...invoices];
    rows.sort((a, b) => (sortDir === 'desc' ? b.invoice_number - a.invoice_number : a.invoice_number - b.invoice_number));
    return rows;
  }, [invoices, sortDir]);

  // Search matches across every visible column — invoice #, dates, amount,
  // customer, notes, send/paid state as words, and the linked contract's
  // label — not just whatever field happens to be selected.
  const visibleInvoices = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortedInvoices;
    return sortedInvoices.filter((inv) => {
      const contract = contracts.find((c) => c.contract_id === inv.contract_id);
      const haystack = [
        inv.invoice_number, fmt(inv.date), fmt(inv.due), fmtMoney(inv.amount), inv.amount,
        inv.customer, inv.notes, inv.send ? 'sent' : 'not sent', inv.paid_status ? 'paid' : 'unpaid',
        contract ? contractLabel(contract) : '',
      ].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [sortedInvoices, search, contracts, firmMap]);

  const totalOutstanding = invoices.filter((i) => !i.paid_status).reduce((s, i) => s + (Number(i.amount) || 0), 0);
  const overdueCount = invoices.filter((i) => !i.paid_status && daysUntil(i.due) !== null && daysUntil(i.due) < 0).length;
  const unsentCount = invoices.filter((i) => !i.send).length;

  // Optimistic local update instead of a full reload — a full load() re-runs
  // three separate BigQuery reads (slow), and on Cloud Run with more than one
  // instance running, a reload can land on a different instance whose
  // in-memory cache hasn't seen this write yet and still serves stale data
  // for up to its TTL. We already know exactly what changed; just apply it.
  async function patchInvoice(invoice_id, fields) {
    await updateInvoice(getToken, invoice_id, fields);
    setInvoices((prev) => prev.map((inv) => (inv.invoice_id === invoice_id ? { ...inv, ...fields } : inv)));
  }

  function patchContractLocal(contract_id, fields) {
    setContracts((prev) => prev.map((c) => (c.contract_id === contract_id ? { ...c, ...fields } : c)));
  }

  const EDIT_FIELDS = [
    { key: 'date',        label: 'Date' },
    { key: 'due',         label: 'Due' },
    { key: 'amount',      label: 'Amount' },
    { key: 'customer',    label: 'Customer' },
    { key: 'notes',       label: 'Notes' },
    { key: 'contract_id', label: 'Contract' },
  ];

  function startEdit(inv) {
    setEditingId(inv.invoice_id);
    setDraft({
      date: toInputDate(inv.date), due: toInputDate(inv.due), amount: String(inv.amount ?? ''),
      customer: inv.customer ?? '', notes: inv.notes ?? '', contract_id: inv.contract_id ?? '',
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setDraft({});
  }

  function fieldDisplay(key, value) {
    if (key === 'date' || key === 'due') return fmt(value);
    if (key === 'amount') return fmtMoney(value);
    if (key === 'contract_id') {
      const c = contracts.find((x) => x.contract_id === value);
      return c ? contractLabel(c) : '(none)';
    }
    return value || '(empty)';
  }

  // Row's "Save" button — builds a plain-English diff and holds it for
  // confirmation instead of writing straight through, per Hazel's request:
  // date/amount edits on a real invoice are exactly the kind of change that
  // deserves a "you sure?" checkpoint rather than an instant silent save.
  function requestSaveEdit(inv) {
    // BigQuery DATE columns arrive as { value: 'YYYY-MM-DD' }, not a plain
    // string — comparing that object directly against draft's plain string
    // always looked "changed" even when it wasn't. Normalize both sides
    // through toInputDate() the same way startEdit() populated the draft.
    const changes = EDIT_FIELDS
      .map(({ key, label }) => {
        const isDate = key === 'date' || key === 'due';
        const before = isDate ? toInputDate(inv[key]) : (inv[key] ?? (key === 'amount' ? 0 : ''));
        return { key, label, before, after: draft[key] };
      })
      .filter(({ key, before, after }) => {
        if (key === 'amount') return Number(before) !== Number(after);
        return String(before ?? '') !== String(after ?? '');
      });
    if (changes.length === 0) { cancelEdit(); return; }
    setPendingChanges({ invoice_id: inv.invoice_id, changes });
  }

  async function confirmSaveEdit() {
    if (!pendingChanges) return;
    const fields = {};
    for (const { key, after } of pendingChanges.changes) {
      fields[key] = key === 'amount' ? Number(after) || 0 : after;
    }
    await patchInvoice(pendingChanges.invoice_id, fields);
    setPendingChanges(null);
    cancelEdit();
  }

  async function handleDelete(invoice_id) {
    if (!confirm('Delete this ENTIRE invoice — date, due, amount, everything? This does not remove a contract file; use "Remove file" inside Manage Contract for that instead.')) return;
    await deleteInvoice(getToken, invoice_id);
    setInvoices((prev) => prev.filter((inv) => inv.invoice_id !== invoice_id));
  }


  function contractLabel(c) {
    if (!c.firm_id) return 'Contract file'; // bare contract auto-created just to hold a file — no firm/terms attached yet
    const firmName = firmMap[c.firm_id]?.firm_name ?? c.firm_id;
    return `${firmName} — ${fmtMoney(c.contract_value)} (ends ${fmt(c.end_date)})`;
  }

  // Every row gets a Contract action regardless of whether one's linked yet —
  // clicking on an unlinked row silently provisions a bare contract record
  // (no firm/value/dates, just something for a file to attach to) and links
  // it, so "manage this company's contract" never requires a detour through
  // row-edit mode to pick one from a dropdown first.
  async function handleManageContract(inv) {
    if (inv.contract_id) {
      setOpenContractFor((id) => (id === inv.invoice_id ? null : inv.invoice_id));
      return;
    }
    setBusyAction(`mkctr-${inv.invoice_id}`);
    try {
      const notes = `Auto-created for invoice #${inv.invoice_number} (${inv.customer ?? 'unknown customer'})`;
      // One BigQuery script (INSERT + UPDATE) instead of two separate
      // requests — each DML statement has its own ~1-2s job-submission
      // overhead, so combining them roughly halves the wait.
      const { contract_id } = await createContractAndLinkInvoice(getToken, { invoice_id: inv.invoice_id, firm_id: '', status: 'active', notes });
      // Optimistic: reflect both writes in local state immediately instead
      // of re-fetching to see the row we already know about.
      setContracts((prev) => [...prev, { contract_id, firm_id: '', contract_value: null, end_date: null, status: 'active', contract_file_url: '', notes }]);
      setInvoices((prev) => prev.map((i) => (i.invoice_id === inv.invoice_id ? { ...i, contract_id } : i)));
      setOpenContractFor(inv.invoice_id);
    } catch (e) {
      setError('Could not create a contract record: ' + e.message);
    } finally {
      setBusyAction(null);
    }
  }

  if (loading) {
    return (
      <div className="loading-page">
        <div className="spinner" />
        <span>Loading invoices…</span>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div style={{
          background: 'var(--red-bg)', color: 'var(--red)', padding: '10px 14px',
          borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 16,
          border: '1px solid rgba(220,38,38,.2)',
        }}>
          {error}
        </div>
      )}

      {/* Summary stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
        <div className="card" style={{ padding: '14px 18px' }}>
          <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--c-primary)' }}>{fmtMoney(totalOutstanding)}</div>
          <div style={{ fontSize: 12, color: 'var(--c-text-2)', marginTop: 2 }}>💰 Outstanding (unpaid)</div>
        </div>
        <div className="card" style={{ padding: '14px 18px' }}>
          <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--c-text)' }}>{invoices.length}</div>
          <div style={{ fontSize: 12, color: 'var(--c-text-2)', marginTop: 2 }}>🧾 Total Invoices</div>
        </div>
        <div className="card" style={{ padding: '14px 18px', borderLeft: unsentCount > 0 ? '3px solid var(--c-amber)' : undefined }}>
          <div style={{ fontSize: 26, fontWeight: 800, color: unsentCount > 0 ? 'var(--c-amber)' : 'var(--c-text)' }}>{unsentCount}</div>
          <div style={{ fontSize: 12, color: 'var(--c-text-2)', marginTop: 2 }}>📤 Not Sent Yet</div>
        </div>
        <div className="card" style={{ padding: '14px 18px', borderLeft: overdueCount > 0 ? '3px solid var(--c-red)' : undefined }}>
          <div style={{ fontSize: 26, fontWeight: 800, color: overdueCount > 0 ? 'var(--c-red)' : 'var(--c-text)' }}>{overdueCount}</div>
          <div style={{ fontSize: 12, color: 'var(--c-text-2)', marginTop: 2 }}>🚨 Overdue &amp; Unpaid</div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="🔍 Search invoices — number, customer, amount, notes…"
          style={{ flex: '1 1 320px', maxWidth: 420, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--c-border)', fontSize: 13 }}
        />
        <button className="btn btn-primary" onClick={() => setShowGenerate(true)}>+ Generate Invoice</button>
      </div>
      {search.trim() && (
        <div style={{ fontSize: 12, color: 'var(--c-text-3)', marginBottom: 8 }}>
          {visibleInvoices.length} of {invoices.length} invoices match "{search.trim()}"
        </div>
      )}

      {showGenerate && (
        <GenerateInvoiceModal
          getToken={getToken}
          clients={clients}
          contracts={contracts}
          firmMap={firmMap}
          contractLabel={contractLabel}
          onCancel={() => setShowGenerate(false)}
          onGenerate={async (payload) => {
            await generateInvoice(getToken, payload);
            setShowGenerate(false);
            await load();
          }}
          onContractFileUploaded={(fields) => patchContractLocal(fields.contract_id, fields)}
        />
      )}

      {pendingChanges && (
        <ConfirmChangesModal
          changes={pendingChanges.changes}
          fieldDisplay={fieldDisplay}
          onCancel={() => setPendingChanges(null)}
          onConfirm={confirmSaveEdit}
        />
      )}


      {/* Invoice table — 1:1 with the Invoice Tracker sheet columns */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ cursor: 'pointer' }} onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}>
                  Invoice {sortDir === 'desc' ? '↓' : '↑'}
                </th>
                <th>Date</th>
                <th>Due</th>
                <th>Amount</th>
                <th>Customer</th>
                <th>Send</th>
                <th>Paid Status</th>
                <th>Notes</th>
                <th>Invoice Record</th>
                <th>Contract</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visibleInvoices.map((inv) => {
                const overdue = !inv.paid_status && daysUntil(inv.due) !== null && daysUntil(inv.due) < 0;
                const contract = contracts.find((c) => c.contract_id === inv.contract_id);
                const editing = editingId === inv.invoice_id;

                if (editing) {
                  return (
                    <tr key={inv.invoice_id} style={{ background: 'var(--orange-light, #fff7ed)' }}>
                      <td style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{inv.invoice_number}</td>
                      <td style={{ minWidth: 130 }}>
                        <input type="date" value={draft.date} onChange={(e) => setDraft((d) => ({ ...d, date: e.target.value }))} style={editInputStyle} />
                      </td>
                      <td style={{ minWidth: 130 }}>
                        <input type="date" value={draft.due} onChange={(e) => setDraft((d) => ({ ...d, due: e.target.value }))} style={editInputStyle} />
                      </td>
                      <td style={{ minWidth: 100 }}>
                        <input type="number" value={draft.amount} onChange={(e) => setDraft((d) => ({ ...d, amount: e.target.value }))} style={editInputStyle} />
                      </td>
                      <td style={{ minWidth: 160 }}>
                        <input value={draft.customer} onChange={(e) => setDraft((d) => ({ ...d, customer: e.target.value }))} style={editInputStyle} />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <input type="checkbox" checked={!!inv.send} onChange={(e) => patchInvoice(inv.invoice_id, { send: e.target.checked })} />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <input type="checkbox" checked={!!inv.paid_status} onChange={(e) => patchInvoice(inv.invoice_id, { paid_status: e.target.checked })} />
                      </td>
                      <td style={{ minWidth: 140 }}>
                        <input value={draft.notes} onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))} style={editInputStyle} />
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--c-text-3)' }}>—</td>
                      <td style={{ minWidth: 160 }}>
                        <select value={draft.contract_id} onChange={(e) => setDraft((d) => ({ ...d, contract_id: e.target.value }))} style={editInputStyle}>
                          <option value="">(none)</option>
                          {contracts.map((c) => <option key={c.contract_id} value={c.contract_id}>{contractLabel(c)}</option>)}
                        </select>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="action-btn" title="Save changes" onClick={() => requestSaveEdit(inv)}>✅</button>
                          <button className="action-btn" title="Cancel" onClick={cancelEdit}>✕</button>
                        </div>
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr key={inv.invoice_id}>
                    <td style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{inv.invoice_number}</td>
                    <td style={{ minWidth: 110 }}>{fmt(inv.date)}</td>
                    <td style={{ minWidth: 110, color: overdue ? 'var(--c-red)' : 'inherit', fontWeight: overdue ? 700 : 400 }}>{fmt(inv.due)}</td>
                    <td style={{ minWidth: 90 }}>{fmtMoney(inv.amount)}</td>
                    <td style={{ minWidth: 160 }}>{inv.customer ?? ''}</td>
                    <td style={{ textAlign: 'center' }}>
                      <input type="checkbox" checked={!!inv.send} onChange={(e) => patchInvoice(inv.invoice_id, { send: e.target.checked })} />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <input type="checkbox" checked={!!inv.paid_status} onChange={(e) => patchInvoice(inv.invoice_id, { paid_status: e.target.checked })} />
                    </td>
                    <td style={{ minWidth: 140 }}>{inv.notes || <span style={{ color: 'var(--c-text-3)' }}>—</span>}</td>
                    <td style={{ fontSize: 12, position: 'relative' }}>
                      <button
                        className="action-btn"
                        style={{ fontSize: 12, textAlign: 'left', padding: 0, background: 'none', border: 'none', color: inv.invoice_record ? 'inherit' : 'var(--c-primary)', cursor: 'pointer' }}
                        onClick={() => setOpenInvoiceFileFor((id) => (id === inv.invoice_id ? null : inv.invoice_id))}
                        title="Manage invoice PDF"
                      >
                        {inv.invoice_record ? 'Invoice PDF 📎' : '+ Manage Invoice'}
                      </button>
                      {openInvoiceFileFor === inv.invoice_id && (
                        <div style={{
                          position: 'absolute', top: '100%', left: 0, zIndex: 20, marginTop: 4,
                          width: 260, background: 'var(--surface)', border: '1px solid var(--c-border-2)',
                          borderRadius: 8, boxShadow: '0 8px 24px rgba(20,30,50,.15)', padding: 12,
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--c-text-3)', textTransform: 'uppercase' }}>Invoice PDF</span>
                            <button className="action-btn" style={{ fontSize: 11 }} onClick={() => setOpenInvoiceFileFor(null)}>✕</button>
                          </div>
                          <InvoiceFileManager
                            invoice={inv}
                            getToken={getToken}
                            onChanged={(fields) => setInvoices((prev) => prev.map((i) => (i.invoice_id === fields.invoice_id ? { ...i, ...fields } : i)))}
                          />
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: 12, minWidth: 140, position: 'relative' }}>
                      <button
                        className="action-btn"
                        style={{ fontSize: 12, textAlign: 'left', padding: 0, background: 'none', border: 'none', color: contract ? 'inherit' : 'var(--c-primary)', cursor: 'pointer' }}
                        disabled={busyAction === `mkctr-${inv.invoice_id}`}
                        onClick={() => handleManageContract(inv)}
                        title="Manage contract file"
                      >
                        {busyAction === `mkctr-${inv.invoice_id}`
                          ? 'Creating…'
                          : contract ? `${contractLabel(contract)} 📎` : '+ Manage Contract'}
                      </button>
                      {openContractFor === inv.invoice_id && contract && (
                        <div style={{
                          position: 'absolute', top: '100%', left: 0, zIndex: 20, marginTop: 4,
                          width: 260, background: 'var(--surface)', border: '1px solid var(--c-border-2)',
                          borderRadius: 8, boxShadow: '0 8px 24px rgba(20,30,50,.15)', padding: 12,
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--c-text-3)', textTransform: 'uppercase' }}>Contract file</span>
                            <button className="action-btn" style={{ fontSize: 11 }} onClick={() => setOpenContractFor(null)}>✕</button>
                          </div>
                          <ContractFileManager contract={contract} getToken={getToken} onChanged={(fields) => patchContractLocal(contract.contract_id, fields)} />
                        </div>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="action-btn" title="Edit invoice" onClick={() => startEdit(inv)}>✏️</button>
                        <button className="action-btn" title="Delete this entire invoice record (date, due, amount, everything) — not just its contract file" onClick={() => handleDelete(inv.invoice_id)}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {visibleInvoices.length === 0 && (
                <tr><td colSpan={11} style={{ textAlign: 'center', color: 'var(--c-text-3)', padding: 20 }}>
                  {search.trim() ? 'No invoices match your search.' : 'No invoices yet. Click "+ Generate Invoice" to create the first one.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Generate Invoice modal ───────────────────────────────────────────────────

const fieldLabel = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--c-text-2)', marginBottom: 4 };
const fieldInput = { width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--c-border)' };
const editInputStyle = { width: '100%', padding: '5px 8px', borderRadius: 6, border: '1px solid var(--c-border-2)', fontSize: 13, fontFamily: 'inherit' };

function GenerateInvoiceModal({ getToken, clients, contracts, firmMap, contractLabel, onCancel, onGenerate, onContractFileUploaded }) {
  const [customer, setCustomer] = useState('');
  const [isNewClient, setIsNewClient] = useState(false);
  const [addr1, setAddr1] = useState('');
  const [addr2, setAddr2] = useState('');
  const [email, setEmail] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [unitPrice, setUnitPrice] = useState('');
  const [termStart, setTermStart] = useState('');
  const [termEnd, setTermEnd] = useState('');
  const [contractId, setContractId] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const amount = (Number(quantity) || 0) * (Number(unitPrice) || 0);
  const description = buildDescription(quantity, termStart, termEnd);
  const selectedContract = contracts.find((c) => c.contract_id === contractId);

  function selectCustomer(name) {
    if (name === '__new__') {
      setIsNewClient(true);
      setCustomer('');
      setAddr1(''); setAddr2(''); setEmail('');
      return;
    }
    setIsNewClient(false);
    setCustomer(name);
    const c = clients.find((cl) => cl.name === name);
    setAddr1(c?.address_line1 ?? '');
    setAddr2(c?.address_line2 ?? '');
    setEmail(c?.email ?? '');
  }

  async function submit() {
    if (!customer || !unitPrice) return;
    setSaving(true);
    setErr(null);
    try {
      await onGenerate({
        customer,
        contract_id: contractId || '',
        notes,
        client: { address_line1: addr1, address_line2: addr2, email },
        lineItems: [{ description, quantity: Number(quantity) || 1, unit_price: Number(unitPrice) || 0 }],
      });
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onCancel(); }}
    >
      <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-md)', padding: '24px 28px', width: 480, maxWidth: '92vw', maxHeight: '86vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>Generate Invoice</div>

        {err && (
          <div style={{ background: 'var(--red-bg)', color: 'var(--red)', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>{err}</div>
        )}

        <label style={fieldLabel}>Customer</label>
        <select
          value={isNewClient ? '__new__' : customer}
          onChange={(e) => selectCustomer(e.target.value)}
          style={{ ...fieldInput, marginBottom: 10 }}
        >
          <option value="">Select a customer…</option>
          {clients.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
          <option value="__new__">+ New customer…</option>
        </select>

        {/* Address/email always shown once a customer is picked (new or existing) —
            editable here, and saved back to billing_clients on submit so the two
            never drift apart. */}
        {(customer || isNewClient) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10, padding: 10, background: 'var(--surface-2, #f8fafc)', borderRadius: 6 }}>
            {isNewClient && (
              <input placeholder="Customer legal name" value={customer} onChange={(e) => setCustomer(e.target.value)} style={fieldInput} />
            )}
            <input placeholder="Address line 1" value={addr1} onChange={(e) => setAddr1(e.target.value)} style={fieldInput} />
            <input placeholder="Address line 2 (city, state, zip)" value={addr2} onChange={(e) => setAddr2(e.target.value)} style={fieldInput} />
            <input placeholder="AP email" value={email} onChange={(e) => setEmail(e.target.value)} style={fieldInput} />
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={fieldLabel}>Quantity (# users)</label>
            <input type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} style={fieldInput} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={fieldLabel}>Unit Price</label>
            <input type="number" value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} style={fieldInput} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={fieldLabel}>Term start</label>
            <input type="date" value={termStart} onChange={(e) => setTermStart(e.target.value)} style={fieldInput} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={fieldLabel}>Term end</label>
            <input type="date" value={termEnd} onChange={(e) => setTermEnd(e.target.value)} style={fieldInput} />
          </div>
        </div>

        {/* Auto-built from quantity + term dates above — matches "Implied Platform
            for Seven Users / June 1, 2026 - May 31, 2027" wording, no manual typing. */}
        <div style={{ fontSize: 12, color: 'var(--c-text-2)', background: 'var(--surface-2, #f8fafc)', borderRadius: 6, padding: '8px 10px', marginBottom: 10, whiteSpace: 'pre-line' }}>
          {description}
        </div>

        <div style={{ fontSize: 13, color: 'var(--c-text-2)', marginBottom: 10 }}>
          Total: <strong style={{ color: 'var(--c-text)' }}>{fmtMoney(amount)}</strong>
        </div>

        <label style={fieldLabel}>Contract (optional)</label>
        <select
          value={contractId}
          onChange={(e) => setContractId(e.target.value)}
          style={{ ...fieldInput, marginBottom: 8 }}
        >
          <option value="">(none)</option>
          {contracts.map((c) => <option key={c.contract_id} value={c.contract_id}>{contractLabel(c)}</option>)}
        </select>

        {selectedContract && (
          <div style={{ marginBottom: 10 }}>
            <ContractFileManager contract={selectedContract} getToken={getToken} onChanged={onContractFileUploaded} />
          </div>
        )}

        <label style={fieldLabel}>Notes (optional)</label>
        <input value={notes} onChange={(e) => setNotes(e.target.value)} style={{ ...fieldInput, marginBottom: 16 }} />

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn" onClick={onCancel} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving || !customer || !unitPrice}>
            {saving ? 'Generating…' : 'Generate & Record'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Confirm-before-save modal ────────────────────────────────────────────────
// A quick "you sure?" checkpoint before an edited invoice's date/amount/etc.
// actually writes to BigQuery — shows exactly what's changing, old vs new.

function ConfirmChangesModal({ changes, fieldDisplay, onCancel, onConfirm }) {
  const [saving, setSaving] = useState(false);

  async function confirm() {
    setSaving(true);
    await onConfirm();
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onCancel(); }}
    >
      <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-md)', padding: '22px 26px', width: 420, maxWidth: '92vw', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Save these changes?</div>
        <div style={{ fontSize: 12, color: 'var(--c-text-3)', marginBottom: 14 }}>This updates the invoice record in BigQuery.</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
          {changes.map(({ key, label, before, after }) => (
            <div key={key} style={{ fontSize: 13 }}>
              <div style={{ fontWeight: 600, color: 'var(--c-text-2)', marginBottom: 2 }}>{label}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: 'var(--c-red)', textDecoration: 'line-through' }}>{fieldDisplay(key, before)}</span>
                <span style={{ color: 'var(--c-text-3)' }}>→</span>
                <span style={{ color: 'var(--c-green)', fontWeight: 600 }}>{fieldDisplay(key, after)}</span>
              </div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn" onClick={onCancel} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={confirm} disabled={saving}>{saving ? 'Saving…' : 'Confirm'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Generic file manager ─────────────────────────────────────────────────────
// Shared by contract files AND invoice PDFs — upload / paste-a-Drive-link /
// download / Save-to-Drive / remove, all wired through whatever backend calls
// the caller supplies. Upload always shows (not just when empty): a wrong or
// outdated file is a "replace it", not "delete the record and start over".

function FileManager({ fileUrl, getToken, downloadName, onUpload, onLink, onDownload, onRemove, onChanged, removeConfirmText }) {
  const [uploadingFile, setUploadingFile] = useState(false);
  const [downloadingFile, setDownloadingFile] = useState(false);
  const [savingToDrive, setSavingToDrive] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [driveLink, setDriveLink] = useState('');
  const [err, setErr] = useState(null);

  async function handleFile(file) {
    setUploadingFile(true);
    setErr(null);
    try {
      const url = await onUpload(file);
      onChanged?.(url);
    } catch (e) {
      setErr('Upload failed: ' + e.message);
    } finally {
      setUploadingFile(false);
    }
  }

  async function handleDriveLinkAttach() {
    if (!driveLink.trim()) return;
    setUploadingFile(true);
    setErr(null);
    try {
      const url = await onLink(driveLink.trim());
      setDriveLink('');
      onChanged?.(url);
    } catch (e) {
      setErr('Attaching Drive link failed: ' + e.message);
    } finally {
      setUploadingFile(false);
    }
  }

  async function handleDownload() {
    setDownloadingFile(true);
    setErr(null);
    try {
      const blob = await onDownload();
      triggerDownload(blob, downloadName);
    } catch (e) {
      setErr('Download failed: ' + e.message);
    } finally {
      setDownloadingFile(false);
    }
  }

  async function handleSaveToDrive() {
    setSavingToDrive(true);
    setErr(null);
    try {
      const token = await getToken();
      let folder; // undefined = picker unavailable (save to root); null = user cancelled; object = chosen
      try {
        folder = await pickDriveFolder(token);
      } catch (e) {
        // Picker not configured (no API key yet) — fall back to Drive root
        // rather than blocking the save entirely.
        console.warn('[FileManager] folder picker unavailable:', e.message);
      }
      if (folder === null) return; // user explicitly cancelled the picker — don't save anywhere
      const blob = await onDownload();
      const { webViewLink } = await saveBlobToDrive(token, downloadName, blob, folder?.id);
      window.open(webViewLink, '_blank');
    } catch (e) {
      setErr('Save to Drive failed: ' + e.message);
    } finally {
      setSavingToDrive(false);
    }
  }

  async function handleRemoveFile() {
    if (!confirm(removeConfirmText)) return;
    setRemoving(true);
    setErr(null);
    try {
      await onRemove();
      onChanged?.('');
    } catch (e) {
      setErr('Remove failed: ' + e.message);
    } finally {
      setRemoving(false);
    }
  }

  return (
    <div>
      {err && <div style={{ fontSize: 11, color: 'var(--c-red)', marginBottom: 6 }}>{err}</div>}
      {fileUrl && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
          {fileUrl.startsWith('gs://') ? (
            <>
              <button className="action-btn" style={{ fontSize: 12 }} disabled={downloadingFile} onClick={handleDownload}>
                {downloadingFile ? 'Downloading…' : '⬇ Download'}
              </button>
              <button className="action-btn" style={{ fontSize: 12 }} disabled={savingToDrive} onClick={handleSaveToDrive}>
                {savingToDrive ? 'Saving…' : '📁 Save to Drive'}
              </button>
            </>
          ) : (
            <a href={fileUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>Open file (Drive link) ↗</a>
          )}
          <button className="action-btn" style={{ fontSize: 12, color: 'var(--c-red)' }} disabled={removing} onClick={handleRemoveFile}>
            {removing ? 'Removing…' : '🗑 Remove file'}
          </button>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <label style={{ fontSize: 12, cursor: 'pointer', color: 'var(--c-primary)' }}>
          {uploadingFile ? 'Uploading…' : fileUrl ? '📎 Replace with a different file' : '📎 Upload a file'}
          <input
            type="file"
            accept="application/pdf,image/*"
            disabled={uploadingFile}
            style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
          />
        </label>
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            placeholder="…or paste a Google Drive share link"
            value={driveLink}
            onChange={(e) => setDriveLink(e.target.value)}
            style={{ ...fieldInput, flex: 1, fontSize: 12 }}
          />
          <button className="btn btn-sm" disabled={uploadingFile || !driveLink.trim()} onClick={handleDriveLinkAttach}>Attach</button>
        </div>
      </div>
    </div>
  );
}

function ContractFileManager({ contract, getToken, onChanged }) {
  if (!contract) return null;
  return (
    <FileManager
      fileUrl={contract.contract_file_url}
      getToken={getToken}
      downloadName={`Contract_${contract.contract_id}.pdf`}
      removeConfirmText='Remove this contract file? The invoice and all its other fields are unaffected — you can attach a new file right after.'
      onUpload={async (file) => (await uploadContractFile(getToken, contract.contract_id, file)).contract_file_url}
      onLink={async (url) => (await linkContractFile(getToken, contract.contract_id, url)).contract_file_url}
      onDownload={() => downloadContractFileBlob(getToken, contract.contract_id)}
      onRemove={() => updateContract(getToken, contract.contract_id, { contract_file_url: '' })}
      onChanged={(contract_file_url) => onChanged?.({ contract_id: contract.contract_id, contract_file_url })}
    />
  );
}

function InvoiceFileManager({ invoice, getToken, onChanged }) {
  if (!invoice) return null;
  return (
    <FileManager
      fileUrl={invoice.invoice_record}
      getToken={getToken}
      downloadName={`Invoice_${invoice.invoice_number}.pdf`}
      removeConfirmText='Remove this invoice PDF? The rest of the invoice (date, due, amount, everything else) is unaffected — you can attach a new file right after, or generate one again.'
      onUpload={async (file) => (await uploadInvoiceFile(getToken, invoice.invoice_id, file)).invoice_record}
      onLink={async (url) => (await linkInvoiceFile(getToken, invoice.invoice_id, url)).invoice_record}
      onDownload={() => downloadInvoicePdfBlob(getToken, invoice.invoice_id)}
      onRemove={() => updateInvoice(getToken, invoice.invoice_id, { invoice_record: '' })}
      onChanged={(invoice_record) => onChanged?.({ invoice_id: invoice.invoice_id, invoice_record })}
    />
  );
}
